import { Button, Col, Row, Stack, TextArea } from '@workramp/ui';
import { useState } from 'react';
import { I18n } from '~/common/explicit-exports';
import { SuggestionChips } from '../SuggestionChips';

export interface ChatInputProps {
  onSubmit: (message: string) => void;
  onCancel?: () => void;
  isSubmitting: boolean;
  placeholder?: string;
  initialValue?: string;
  onChange?: (value: string) => void;
  suggestions?: string[];
}

export function ChatInput({
  onSubmit,
  onCancel,
  isSubmitting,
  placeholder,
  initialValue,
  onChange,
  suggestions = [],
}: ChatInputProps) {
  const [message, setMessage] = useState(initialValue || '');

  const handleSubmit = () => {
    if (!message.trim()) return;
    onSubmit(message.trim());
    setMessage('');
    onChange?.('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSelectSuggestion = (suggestion: string) => {
    setMessage(suggestion);
    onChange?.(suggestion);
  };

  return (
    <Stack size={2}>
      <TextArea
        label="Message" // Required prop for TextArea
        hideLabel
        value={message}
        onChange={setMessage}
        onKeyDown={handleKeyDown}
        placeholder={
          placeholder ||
          I18n.t('js.admin.content_creation.chat_input_placeholder', { defaultValue: 'Type your message...' })
        }
        rows={3}
        isDisabled={isSubmitting}
      />
      {suggestions && suggestions.length > 0 && (
        <SuggestionChips suggestions={suggestions} onSelectSuggestion={handleSelectSuggestion} />
      )}
      <Row justify="end" gapX={2}>
        <Col xs="auto">
          {onCancel && (
            <Button color="secondary" variant="text" onPress={onCancel}>
              Cancel
            </Button>
          )}
        </Col>
        <Col xs="auto">
          <Button color="primary" variant="solid" onPress={handleSubmit} isDisabled={!message.trim() || isSubmitting}>
            {isSubmitting
              ? I18n.t('js.common.loading', { defaultValue: 'Loading...' })
              : I18n.t('js.admin.content_creation.send', { defaultValue: 'Send' })}
          </Button>
        </Col>
      </Row>
    </Stack>
  );
}
